import { useEffect, useState } from 'react';
import { CALCULATORS } from '../../../../../constants';
import { CalculatorConfigType, CALCULATORS_NAMES } from '../calculatorsTypes';
import Hotkeys from 'react-hot-keys';
import RightPopUp from '../../../../common/RightPopUp';
import Calculator, { DefaultValuesForCalculator, UnitObject, ValueObject } from '../Calculators';
import { CalculatorMetaDataType } from './CalculatorResults';
import {
    getPatientPersonalProfile,
    getVitals,
} from '../../../../../store/selectors/prescriptionPad';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { CalculatorEntity } from '../../../../../store/types/prescription';

const WEIGHT_ID = 'lb-7991006736';
const HEIGHT_ID = 'lb-7561569471';
const WEIGHT_KG_UNIT_CODE = '258683005';
const HEIGHT_CM_UNIT_CODE = '258672001';

interface ObjectType {
    [key: string]: string;
}

export const CalculatorRightPopUp: React.FC<{
    onElementUpdate: (
        option: CalculatorEntity,
        index?: number,
        itemIdx?: number,
        valueObject?: ValueObject,
        unitObject?: UnitObject,
    ) => void;
    calculatorName: CALCULATORS_NAMES;
    closeMe: () => void;
    propsDefaultValuesForCalculator?: DefaultValuesForCalculator;
    propsDefaultUnitsForCalculator?: ObjectType;
}> = ({
    calculatorName,
    closeMe,
    onElementUpdate,
    propsDefaultValuesForCalculator,
    propsDefaultUnitsForCalculator,
}) => {
    const personalDetails = useSelector(getPatientPersonalProfile);

    const calculatorType = CALCULATORS.find((calc) => calc.name === calculatorName);
    const [config, setConfig] = useState<CalculatorConfigType | undefined>(undefined);
    const vitals = useSelector(getVitals);
    const weightVital = vitals?.find((i) => i.id === WEIGHT_ID);
    const heightVital = vitals?.find((i) => i.id === HEIGHT_ID);
    const weightVitalValue =
        weightVital?.value?.code === WEIGHT_KG_UNIT_CODE ? weightVital.value.qt : undefined;
    const heightVitalValue =
        heightVital?.value?.code === HEIGHT_CM_UNIT_CODE ? heightVital.value.qt : undefined;

    const defaultValuesForCalculator = {
        age: {
            value: moment().diff(personalDetails?.age?.dob, 'years').toString(),
        },
        gender: {
            value: personalDetails?.gender,
        },
        weight: { value: weightVitalValue },
        height: {
            value: heightVitalValue,
        },
        ...propsDefaultValuesForCalculator,
    };

    const defaultUnitsForCalculator = {
        ...propsDefaultUnitsForCalculator,
    };

    useEffect(() => {
        async function fetchCalculatorData(calculatorType: CalculatorMetaDataType) {
            try {
                const response = await fetch(calculatorType.api);
                const data = await response.json();
                setConfig(data);
            } catch (error) {
                console.log(error);
            }
        }

        calculatorType && fetchCalculatorData(calculatorType);
    }, [calculatorType]);

    if (!config) {
        return null;
    }

    return (
        <Hotkeys keyName="esc" onKeyDown={closeMe}>
            <RightPopUp closeMe={closeMe}>
                <div className="bg-darwin-neutral-50 flex flex-col h-full space-between">
                    <div className="flex flex-col w-full h-full px-16 py-18 bg-darwin-neutral-50 text-14 font-400">
                        <div className="flex items-center space-x-16">
                            <svg
                                onClick={closeMe}
                                className="w-13 h-14 fill-darwin-neutral-1000 cursor-pointer"
                                viewBox="0 0 13 12"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M12.5977 5.71875C12.5977 6.10156 12.2969 6.40234 11.9414 6.40234H2.64453L6.28125 9.84766C6.55469 10.0938 6.55469 10.5312 6.30859 10.7773C6.0625 11.0508 5.65234 11.0508 5.37891 10.8047L0.566406 6.21094C0.429688 6.07422 0.375 5.91016 0.375 5.71875C0.375 5.55469 0.429688 5.39062 0.566406 5.25391L5.37891 0.660156C5.65234 0.414062 6.0625 0.414062 6.30859 0.6875C6.55469 0.933594 6.55469 1.37109 6.28125 1.61719L2.64453 5.0625H11.9414C12.3242 5.0625 12.5977 5.36328 12.5977 5.71875Z" />
                            </svg>
                            <p className="text-16 font-700">
                                {calculatorType?.title?.toUpperCase()}
                            </p>
                        </div>

                        {config?.calculator?.ekaDescription ? (
                            <div className="flex items-center space-x-10 p-12 bg-white mt-12 mb-16 rounded-4">
                                <svg
                                    className="min-w-14 max-w-14 h-14 fill-darwin-neutral-900"
                                    viewBox="0 0 15 15"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M7.5 0.75C3.61719 0.75 0.5 3.89453 0.5 7.75C0.5 11.6328 3.61719 14.75 7.5 14.75C11.3555 14.75 14.5 11.6328 14.5 7.75C14.5 3.89453 11.3555 0.75 7.5 0.75ZM7.5 4.25C7.96484 4.25 8.375 4.66016 8.375 5.125C8.375 5.61719 7.96484 6 7.5 6C7.00781 6 6.625 5.61719 6.625 5.125C6.625 4.66016 7.00781 4.25 7.5 4.25ZM8.59375 11.25H6.40625C6.02344 11.25 5.75 10.9766 5.75 10.5938C5.75 10.2383 6.02344 9.9375 6.40625 9.9375H6.84375V8.1875H6.625C6.24219 8.1875 5.96875 7.91406 5.96875 7.53125C5.96875 7.17578 6.24219 6.875 6.625 6.875H7.5C7.85547 6.875 8.15625 7.17578 8.15625 7.53125V9.9375H8.59375C8.94922 9.9375 9.25 10.2383 9.25 10.5938C9.25 10.9766 8.94922 11.25 8.59375 11.25Z" />
                                </svg>
                                <p>{config?.calculator?.ekaDescription}</p>
                            </div>
                        ) : (
                            <div className="mb-24"></div>
                        )}

                        <div className="flex-1 flex gap-x-16 overflow-hidden">
                            {calculatorType && (
                                <Calculator
                                    onElementUpdate={onElementUpdate}
                                    calculatorType={calculatorType}
                                    defaultValuesForCalculator={defaultValuesForCalculator}
                                    closeMe={closeMe}
                                    defaultUnitsForCalculator={defaultUnitsForCalculator}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </RightPopUp>
        </Hotkeys>
    );
};
